/**
 * `Sheet`: the frame a dialog's content sits in, a library member (COMPONENT_LIBRARY.md §4).
 * Promoted from My Orders' `frames/Sheet` in L0 (2026-10-06, the user: "meet the rules, look
 * same"), which is now a thin wrapper over this one passing My Orders' own values.
 *
 * The PROPS, the header and both containers are shared: the container is chosen by the window's SIZE, not
 * by platform (`SizedSheet`; layout by screen size, CLAUDE.md): a bottom sheet in a compact window, a centred
 * dialog in a wider one. Only a phone's `portal` presentation (gorhom, `index.native.tsx`) is a platform API.
 *
 * ⚠️ A sheet STAYS MOUNTED and is toggled by `visible`: both containers animate out, and neither
 * can animate a component its parent has just unmounted. So a sheet's content re-initialises its
 * own fields in an effect on `visible`, not on a fresh mount.
 */
import React, { createContext, useContext } from "react";
import type { DimensionValue } from "react-native";
import { Modal, Pressable, StyleSheet, Text, View, useWindowDimensions } from "react-native";

import { DirectionRoot, useDirection } from "@rista/ui/i18n";
import { IconButton } from "@rista/ui/library/components/IconButton";
import { MODAL_ORIENTATIONS } from "@rista/ui/library/components/ModalOrientations";
import { useCompact } from "@rista/ui/library/layout/useCompact";
import { useKeyboardOverlap } from "@rista/ui/library/layout/useKeyboardOverlap";
import { useTheme } from "@rista/ui/library/theme/ThemeProvider";
import { RADIUS, SHADOWS } from "@rista/ui/library/theme/tokens";
import type { Theme } from "@rista/ui/library/theme/theme";
import { lookFor, type SlotStyle } from "@rista/ui/library/theme/variants";
import { sheetLegacyLook } from "./legacyLook";
import { sheetLook } from "./look";

export interface SheetProps {
  visible: boolean;
  onClose: () => void;
  /** What a screen reader says for the backdrop that closes it, and for the close button when `closeButton` is on: the host's words (rule: words passed in). */
  closeLabel: string;
  title?: string;
  subtitle?: string;
  /**
   * The centred dialog only: a ✕ that closes it, labelled `closeLabel`, floating OUTSIDE the dialog, above its
   * top-end corner, on the scrim. A bottom sheet draws none (the user, 2026-10-21): it closes by a pan down, the
   * backdrop or hardware back. Off by default. Not `closeButton`, which sits inside the header in every form.
   */
  showClose?: boolean;
  /** On the dialog; the backdrop gets `<testID>-backdrop`, the ✕ (`showClose` or `closeButton`) `<testID>-close`. */
  testID?: string;
  /** `alertdialog` for a warning that interrupts; `dialog` otherwise. */
  role?: "dialog" | "alertdialog";
  /**
   * On the centred dialog (a window wider than compact, either app): `wide` (half the window, at most 720: My Orders' sheets), `narrow` (at most 500: a warning),
   * `large` (80 % of the window both ways, square: web v1's customise popup, New Order v2's customiser) or
   * `panel` (the same box with rounded corners: the discount sheet, as web v1's cards). A compact window
   * draws a bottom sheet instead and ignores it.
   */
  size?: SheetSize;
  /**
   * In a compact NATIVE window: `portal` (the default) draws a `@gorhom/bottom-sheet` modal through the
   * provider the app mounts at its root; `modal` draws the bottom sheet inside a React Native `Modal`, above
   * every other modal, for content that must show whatever is open (v2's alert). A wider window ignores it, and
   * compact web has no gorhom provider, so it always draws the `Modal` one.
   */
  presentation?: "portal" | "modal";
  /**
   * In a compact native window, `portal` only: gorhom renders the body in its provider's portal, outside any
   * context between that provider and the sheet, so a host wraps the body to provide it again.
   */
  wrapContent?: (body: React.ReactNode) => React.ReactNode;
  /**
   * The header's and the bottom sheet handle's look: `legacy` (the default), as the live My Orders sheets draw
   * them; `pos`, the design system's roles, for v2 (DESIGN_SYSTEM.md §7.2, QD5). The frame is the same.
   */
  appearance?: SheetAppearance;
  /**
   * Draw a close button (an X) at the header's end, labelled `closeLabel`, `<testID>-close`. Off by default: a sheet
   * closes by its backdrop, Escape or hardware back, and only a host that asks (the discount sheet, v1's cards
   * have a cross) gets the button. With it the header draws even with no title.
   */
  closeButton?: boolean;
  /**
   * Keep the sheet clear of the on-screen keyboard: the bottom sheet stands on it and the centred dialog centres
   * above it, shrinking to the room left (its body scrolls). Off by default, and only Reservations turns it on so
   * far (RESERVATIONS_V2_PLAN.md decision 189: the user chose its sheets alone); the other flows keep today's sheet.
   * Inert on web, whose `Keyboard` never reports one.
   */
  avoidKeyboard?: boolean;
  children: React.ReactNode;
}

export type SheetAppearance = "legacy" | "pos";

/** the frame's styles, the same for every caller, and the chrome's for an appearance */
export function sheetStyles(theme: Theme, appearance: SheetAppearance = "legacy") {
  const frame = lookFor(sheetLook, theme);
  const paint: Record<"header" | "title" | "subtitle" | "handle", SlotStyle> =
    appearance === "pos" ? frame : lookFor(sheetLegacyLook, theme);
  // the close row's layout is the same in both appearances, so it always comes from the roles' look
  const chrome = { ...paint, headerRow: frame.headerRow, headerText: frame.headerText };
  return { frame, chrome };
}

export type SheetSize = "wide" | "narrow" | "large" | "panel";

/** The centred dialog's box per size: `wide` is My Orders' sheet as it was; `narrow` web v1's warning modal (MUI). */
export const DIALOG_SIZES: Record<SheetSize, { width: DimensionValue; height?: DimensionValue; maxWidth: number; borderRadius: number; boxShadow?: string }> = {
  wide: { width: "50%", maxWidth: 720, borderRadius: RADIUS.xl },
  large: { width: "80%", height: "80%", maxWidth: 1600, borderRadius: 0 },
  panel: { width: "80%", height: "80%", maxWidth: 1600, borderRadius: RADIUS.xl },
  narrow: { width: "92%", maxWidth: 500, borderRadius: RADIUS.lg, boxShadow: SHADOWS.dialog },
};

/** `<testID>-backdrop`, or nothing */
export const backdropTestID = (testID?: string) => (testID ? `${testID}-backdrop` : undefined);

/** `<testID>-close` (the `showClose` or `closeButton` ✕), or nothing */
export const closeTestID = (testID?: string) => (testID ? `${testID}-close` : undefined);

/** `<testID>-keyboardSpace` (the `avoidKeyboard` spacer), or nothing */
export const keyboardSpaceTestID = (testID?: string): string | undefined => (testID ? `${testID}-keyboardSpace` : undefined);

type SheetHeaderProps = Pick<SheetProps, "title" | "subtitle" | "closeButton" | "onClose" | "closeLabel" | "testID"> & {
  chrome: ReturnType<typeof sheetStyles>["chrome"];
};

export function SheetHeader({ title, subtitle, closeButton = false, onClose, closeLabel, testID, chrome }: SheetHeaderProps) {
  if (!title && !subtitle && !closeButton) return null;
  const words = (
    <>
      {!!title && (
        <Text role="heading" style={chrome.title}>
          {title}
        </Text>
      )}
      {!!subtitle && <Text style={chrome.subtitle}>{subtitle}</Text>}
    </>
  );
  if (!closeButton) return <View style={chrome.header}>{words}</View>;
  return (
    <View style={[chrome.header, chrome.headerRow]}>
      <View style={chrome.headerText}>{words}</View>
      <IconButton icon="close" size="sm" label={closeLabel} onPress={onClose} testID={closeTestID(testID)} />
    </View>
  );
}

/**
 * `avoidKeyboard` in a `Modal` form: the column's last child, as tall as the keyboard reaches into the `Modal`, so
 * the dialog centres, or the bottom sheet stands, above the keyboard. It measures its own bottom edge, which stays
 * the `Modal`'s bottom however tall it grows (`useKeyboardOverlap`: a `Modal` is drawn behind the navigation bar, so
 * the keyboard's height alone would leave the bar's strip covered).
 */
function KeyboardOverlapSpacer({ testID }: { testID?: string }) {
  const { overlap, onLayout } = useKeyboardOverlap();
  return <View style={{ height: overlap }} onLayout={onLayout} pointerEvents="none" testID={keyboardSpaceTestID(testID)} />;
}

/**
 * The centred dialog: a transparent `Modal` over a backdrop that closes it on a press, Escape or hardware back
 * through `onRequestClose`. `presentation` and `wrapContent` belong to the gorhom sheet and are ignored here.
 *
 * `showClose`: the dialog clips its content (`overflow: "hidden"`, for its rounded corners), so a ✕ outside it
 * cannot be the dialog's child. An unclipped ANCHOR takes the dialog's place and box, the dialog fills it, and
 * the ✕ hangs above the anchor's top-end corner.
 */
function DialogSheet({
  visible,
  onClose,
  closeLabel,
  title,
  subtitle,
  testID,
  role = "dialog",
  size = "wide",
  appearance,
  closeButton,
  showClose,
  avoidKeyboard = false,
  children,
}: SheetProps) {
  const { frame, chrome } = sheetStyles(useTheme(), appearance);
  // a Modal renders outside the screen's DirectionRoot (a portal on web, its own window on native): carry the
  // direction in, read here where the dialog is declared (RTL-ready UI)
  const direction = useDirection();
  // with the keyboard's spacer below it, the outermost box gives up height rather than run under the keyboard
  const shrinkToRoom = avoidKeyboard && styles.shrinkToRoom;
  const dialog = (
    <View role={role} aria-modal style={[frame.dialog, DIALOG_SIZES[size], showClose ? styles.fillAnchor : shrinkToRoom]} testID={testID}>
      <SheetHeader title={title} subtitle={subtitle} closeButton={closeButton} onClose={onClose} closeLabel={closeLabel} testID={testID} chrome={chrome} />
      {children}
    </View>
  );
  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      supportedOrientations={MODAL_ORIENTATIONS}
      onRequestClose={onClose}
    >
      <DirectionRoot direction={direction} style={frame.scrim}>
        <Pressable
          style={StyleSheet.absoluteFill}
          onPress={onClose}
          accessibilityLabel={closeLabel}
          testID={backdropTestID(testID)}
        />
        {showClose ? (
          <View style={[styles.anchor, DIALOG_SIZES[size], styles.unpainted, shrinkToRoom]}>
            {dialog}
            <View style={styles.close}>
              <IconButton icon="close" variant="secondary" size="sm" label={closeLabel} onPress={onClose} testID={closeTestID(testID)} />
            </View>
          </View>
        ) : (
          dialog
        )}
        {avoidKeyboard ? <KeyboardOverlapSpacer testID={testID} /> : null}
      </DirectionRoot>
    </Modal>
  );
}

/**
 * The bottom sheet in a React Native `Modal`, which opens its own window and so sits above everything, v1's
 * modals included. For content that must show whatever is open: v2's alert (the user, 2026-10-06), which a
 * print skip notice can raise from inside a v1 modal. Closed by the backdrop or hardware back
 * (`onRequestClose`). `size` is the dialog's and is ignored here.
 */
function ModalBottomSheet({ visible, onClose, closeLabel, title, subtitle, testID, role = "dialog", appearance, closeButton, avoidKeyboard = false, children }: SheetProps) {
  const { frame, chrome } = sheetStyles(useTheme(), appearance);
  const maxHeight = useWindowDimensions().height * 0.9;
  // a Modal renders outside the screen's DirectionRoot (a portal on web, its own window on native): carry the
  // direction in, read here where the dialog is declared (RTL-ready UI)
  const direction = useDirection();
  return (
    <Modal
      visible={visible}
      transparent
      animationType="slide"
      statusBarTranslucent
      supportedOrientations={MODAL_ORIENTATIONS}
      onRequestClose={onClose}
    >
      <DirectionRoot direction={direction} style={[frame.scrim, styles.scrim]}>
        <Pressable
          style={StyleSheet.absoluteFill}
          onPress={onClose}
          accessibilityLabel={closeLabel}
          testID={backdropTestID(testID)}
        />
        <View role={role} style={[frame.sheet, styles.modalSheet, { maxHeight }, avoidKeyboard && styles.shrinkToRoom]} testID={testID}>
          <View style={[chrome.handle, styles.modalHandle]} />
          <SheetHeader title={title} subtitle={subtitle} closeButton={closeButton} onClose={onClose} closeLabel={closeLabel} testID={testID} chrome={chrome} />
          {children}
        </View>
        {avoidKeyboard ? <KeyboardOverlapSpacer testID={testID} /> : null}
      </DirectionRoot>
    </Modal>
  );
}

/**
 * The sheet's form follows the WINDOW, not the platform (layout by screen size, CLAUDE.md; the user,
 * 2026-10-14: "we shouldnt split Sheet by platform, instead by resolution"): a compact window (768 or
 * narrower, `useCompact`) gets a bottom sheet, a wider one the centred dialog with its `size`. A
 * 1280-wide Android tablet therefore gets the dialog, and a 700-wide Electron window the bottom sheet.
 */
export function SizedSheet(props: SheetProps) {
  return useCompact() ? <ModalBottomSheet {...props} /> : <DialogSheet {...props} />;
}

const CLOSE_GAP = 8;
/** the `sm` IconButton's height */
const CLOSE_SIZE = 36;

const styles = StyleSheet.create({
  // `showClose`: the dialog's placement (`look.ts` › `dialog`), on the unclipped anchor
  anchor: { alignSelf: "center", marginTop: "auto", marginBottom: "auto", maxHeight: "85%" },
  // the anchor takes the size's box, never its paint
  unpainted: { borderRadius: 13, boxShadow: "none" },
  // the dialog inside the anchor: its whole box, shrinking to the anchor's height cap
  fillAnchor: { width: "100%", maxWidth: "100%", height: "100%", maxHeight: "100%", marginTop: 0, marginBottom: 0, flexShrink: 1 },
  // beside the anchor's top-end corner, outside it (a number: a "px" string is web-only, and this runs on native too)
  close: { position: "absolute", top: -(CLOSE_SIZE + CLOSE_GAP), end: -(CLOSE_SIZE + CLOSE_GAP + 1) },
  // `avoidKeyboard`: the dialog or bottom sheet shrinks to the room above the keyboard's spacer; with no keyboard it
  // already fits (85 % or 90 % of the window), so it never does
  shrinkToRoom: { flexShrink: 1 },
  // the bottom sheet's layout; its paint is the look's (`look.ts`, `legacyLook.ts`)
  scrim: { justifyContent: "flex-end" },
  modalSheet: { overflow: "hidden" },
  // gorhom draws its own indicator; the modal sheet draws the same bar
  modalHandle: { height: 4, borderRadius: RADIUS.pill, alignSelf: "center", marginTop: 10, marginBottom: 6 },
});

/**
 * Inside `InlineSheets`, a sheet (and a web `Menu`) draws its frame IN PLACE while `visible`, with no Modal,
 * scrim or portal: the design gallery uses it to show every sheet's states side by side on its page, and so
 * that its HTML export captures them. Nothing in an app mounts it.
 */
const InlineSheetsContext = createContext(false);

export function InlineSheets({ children }: { children: React.ReactNode }) {
  return <InlineSheetsContext.Provider value>{children}</InlineSheetsContext.Provider>;
}

/** Whether a frame should draw in place (see `InlineSheets`). */
export const useInlineSheets = () => useContext(InlineSheetsContext);

/** In place, a dialog takes the width it is given, up to its size's `maxWidth`. */
const INLINE_WIDTH = { width: "100%" };

/** The dialog drawn in place: the web dialog's box and chrome, at the width it is given, up to its size's limit. */
export function InlineSheet({ visible, onClose, closeLabel, title, subtitle, testID, role = "dialog", size = "wide", appearance, closeButton, children }: SheetProps) {
  const { frame, chrome } = sheetStyles(useTheme(), appearance);
  if (!visible) return null;
  return (
    <View role={role} style={[frame.dialog, DIALOG_SIZES[size], INLINE_WIDTH]} testID={testID}>
      <SheetHeader title={title} subtitle={subtitle} closeButton={closeButton} onClose={onClose} closeLabel={closeLabel} testID={testID} chrome={chrome} />
      {children}
    </View>
  );
}
